// SignatureField (job page round 2: the estimate builder's Sign step): where the customer signs. A line
// to sign on, named for who signs ("Jordan Harper signs here"), drawn with a finger, a pen or a mouse.
// Someone who can't draw types their name instead, and it is set in the signature's place. Clear starts
// again. Once signed it says who signed and when, in words; a red line only if it is needed and
// missing. The drawn mark takes the page's ink (the canvas reads its own colour from --sign-ink), so it
// is right in light and dark. Reads only --sign-*; TextField and Link read their own.
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { TextField } from '../Field/Field.tsx';
import { Link } from '../Link/Link.tsx';
import './SignatureField.css';

export type SignatureValue = { kind: 'drawn' } | { kind: 'typed'; name: string } | null;

export interface SignatureFieldProps {
  /** Who signs: "Jordan Harper". */
  signer: string;
  value: SignatureValue;
  onChange: (v: SignatureValue) => void;
  /** Once signed: "Mon Oct 26, 9:14 a.m.". */
  signedAt?: string;
  /** Asked for and missing: "Jordan needs to sign before it can be sold." */
  error?: string;
  /** Start typed (a story, or a customer on the phone). */
  defaultMode?: 'draw' | 'type';
}

export function SignatureField({
  signer,
  value,
  onChange,
  signedAt,
  error,
  defaultMode = 'draw',
}: SignatureFieldProps) {
  const [mode, setMode] = useState(value?.kind === 'typed' ? 'type' : defaultMode);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const box = c.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    c.width = Math.max(1, Math.round(box.width * scale));
    c.height = Math.max(1, Math.round(box.height * scale));
    const g = c.getContext('2d');
    if (!g) return;
    g.scale(scale, scale);
    const css = getComputedStyle(c);
    g.strokeStyle = css.color;
    g.lineWidth = parseFloat(css.getPropertyValue('--sign-stroke')) || 2;
    g.lineCap = 'round';
    g.lineJoin = 'round';
  }, [mode]);

  const at = (e: PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    const g = e.currentTarget.getContext('2d');
    if (!g) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const [x, y] = at(e);
    g.beginPath();
    g.moveTo(x, y);
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const g = e.currentTarget.getContext('2d');
    if (!g) return;
    const [x, y] = at(e);
    g.lineTo(x, y);
    g.stroke();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange({ kind: 'drawn' });
  };
  const clear = () => {
    const c = canvas.current;
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    onChange(null);
  };

  const signed = value !== null;
  const typedName = value?.kind === 'typed' ? value.name : '';
  return (
    <div className="fl-sign" data-bad={error && !signed ? '' : undefined}>
      <p className="fl-sign__label" id="fl-sign-label">
        {signer} signs here
      </p>
      {mode === 'draw' ? (
        <canvas
          ref={canvas}
          className="fl-sign__pad"
          aria-labelledby="fl-sign-label"
          role="img"
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
        />
      ) : (
        <div className="fl-sign__typed">
          <span className="fl-sign__script" aria-hidden="true">
            {typedName}
          </span>
          <TextField
            label={`${signer}’s name, typed as the signature`}
            value={typedName}
            onChange={(name) => onChange(name.trim() ? { kind: 'typed', name } : null)}
          />
        </div>
      )}
      <p className="fl-sign__foot">
        {signed ? (
          <span>
            Signed by {value?.kind === 'typed' ? value.name : signer}
            {signedAt ? ` · ${signedAt}` : ''}
          </span>
        ) : (
          <span className="fl-sign__muted">Not signed yet</span>
        )}
        <span className="fl-sign__acts">
          {signed ? <Link onPress={clear}>Clear</Link> : null}
          <Link
            onPress={() => {
              onChange(null);
              setMode(mode === 'draw' ? 'type' : 'draw');
            }}
          >
            {mode === 'draw' ? 'Type the name instead' : 'Draw it instead'}
          </Link>
        </span>
      </p>
      {error && !signed ? (
        <p className="fl-sign__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
